import React, { Fragment, useMemo, useRef } from 'react';
import {
  Blank,
  ButtonsWrapper,
  CardContainer,
  CardFooter,
  CardText,
  CardWrapper,
  PlayerName,
  SelectedIcon,
} from './Card.styles';
import { CARD_TYPE, GameCard } from '../../types/Card';
import { useGameContext } from '../../contexts/GameContext';
import { Fab, Zoom } from '@mui/material';
import Icon from '../ui/Icon';
import BrandMark from '../ui/BrandMark';
import { APP_NAME } from '../../brand';

export type CardProps = GameCard & {
  totalCards?: number;
  handPosition?: number;
  onOpen?: () => void;
  onConfirmSelect?: () => void;
  onCancelSelect?: () => void;
  open?: boolean;
  player?: string;
}

const Card: React.FC<CardProps> = ({
  handPosition,
  totalCards,
  type = CARD_TYPE.ANSWER,
  text,
  onOpen,
  id,
  onConfirmSelect,
  onCancelSelect,
  open,
  player
}) => {
  const { selectedCards } = useGameContext();

  // the press must start on THIS card. a fanned card jumps out from under the
  // pointer as soon as it lifts, and a touch release then synthesises a click
  // on whatever card is now under the finger -- that stray click opened the
  // card behind. no pointerdown here => not our click.
  const pressedRef = useRef(false);

  const handlePointerDown = () => {
    pressedRef.current = true;
  };

  const handleClick = () => {
    if (!pressedRef.current) return;
    pressedRef.current = false;
    onOpen?.();
  };

  const selected = !!selectedCards?.find((c) => id === c.id);

  const textWithUnderscore = useMemo(() => {
    let returnText = text;
    while (returnText.includes("__")) {
      returnText = returnText.replace(/__/g, "_");
    }
    return returnText;
  }, [text]);

  return (
    <CardWrapper
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      $open={open}
      $handPosition={handPosition}
      $totalCards={totalCards}
    >
      <CardContainer
        $selected={selected}
        $type={type}
        $handPosition={handPosition}
        $totalCards={totalCards}
        $open={open}
        className={open ? "open" : ""}
      >
        <CardText>
          {textWithUnderscore.split("_").map((part, i) =>
            <Fragment key={i}>
              {(textWithUnderscore.startsWith("_") || !!i) && <Blank $type={type} />}
              {part}
            </Fragment>
          )}
        </CardText>

        <CardFooter $type={type}>
          {player ? (
            <PlayerName>
              <Icon name="you" size={12} strokeWidth={3} />
              {player}
            </PlayerName>
          ) : (
            <>
              <BrandMark size={14} />
              <span>{APP_NAME}</span>
            </>
          )}
        </CardFooter>

        {selected && (
          <SelectedIcon>
            <span>
              <Icon name="check" size={34} strokeWidth={3.5} />
            </span>
          </SelectedIcon>
        )}
      </CardContainer>

      <ButtonsWrapper $visible={open} onClick={(e) => e.stopPropagation()}>
        <Zoom
          in={open}
          timeout={300}
          style={{
            transitionDelay: "200ms",
          }}
          unmountOnExit
        >
          <Fab
            color="error"
            aria-label="Cancelar"
            onClick={onCancelSelect}
            size="medium"
          >
            <Icon name="close" size={22} />
          </Fab>
        </Zoom>
        <Zoom
          in={open}
          timeout={300}
          style={{
            transitionDelay: "400ms",
          }}
          unmountOnExit
        >
          <Fab
            color="success"
            aria-label="Escolher carta"
            onClick={onConfirmSelect}
            size="medium"
          >
            <Icon name="check" size={22} strokeWidth={3} />
          </Fab>
        </Zoom>
      </ButtonsWrapper>
    </CardWrapper>
  );
}

export default Card;
